import { formatSubdomainLabel } from '@/constants/healthDomains';
import {
  formatContentDateTime,
  getContentDomainsLabel,
  getContentTypeLabel,
  getContentVisibilityLabel,
} from '@/modules/content/common/contentDisplay';
import { TContentAuditLogEntry, TContentDetail } from '@/types/Content.types';
import { Box, Divider, HStack, Text, VStack } from '@chakra-ui/react';
import React from 'react';

const InfoRow = ({ label, value }: { label: string; value?: string | null }) => (
  <HStack align="flex-start" width="100%" gap="16px">
    <Text minWidth="120px" color="text.mediumGray" fontWeight={600} fontSize="13px">
      {label}
    </Text>
    <Text flex={1} color="text.darkBlue" fontSize="13px" whiteSpace="pre-wrap">
      {value || '/'}
    </Text>
  </HStack>
);

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
  <Text fontWeight={700} fontSize="15px" color="text.darkBlue" marginTop="8px">
    {children}
  </Text>
);

const AuditLogList = ({ entries }: { entries?: TContentAuditLogEntry[] }) => {
  if (!entries?.length) {
    return (
      <Text color="text.mediumGray" fontSize="13px">
        No audit entries yet.
      </Text>
    );
  }

  return (
    <VStack align="stretch" gap="10px" width="100%">
      {entries.map((entry) => (
        <Box
          key={entry.id}
          padding="10px 12px"
          borderRadius="8px"
          backgroundColor="background.lightGray"
          border="1px solid"
          borderColor="background.separatorBlue"
        >
          <HStack justify="space-between" align="flex-start">
            <Text fontWeight={700} fontSize="13px" color="text.darkBlue" textTransform="capitalize">
              {entry.action}
            </Text>
            <Text fontSize="11px" color="text.mediumGray">
              {formatContentDateTime(entry.createdAt)}
            </Text>
          </HStack>
          {!!entry.diffSummary?.length && (
            <VStack align="stretch" gap="2px" marginTop="6px">
              {entry.diffSummary.map((line, index) => (
                <Text key={index} fontSize="12px" color="text.mediumBlue">
                  • {line}
                </Text>
              ))}
            </VStack>
          )}
          <Text fontSize="11px" color="text.mediumGray" marginTop="6px">
            By {entry.performedByUserId}
          </Text>
        </Box>
      ))}
    </VStack>
  );
};

const ModerationInfoPanel = ({ content }: { content: TContentDetail }) => {
  const isRemoved = content.status === 'removed';
  const isWithdrawn = content.status === 'withdrawn';

  // Withdrawal is the author's own act, so the author summary names the actor
  // whenever the ids line up; an admin-triggered withdrawal falls back to the id.
  const withdrawnBy =
    content.withdrawnByUserId && content.withdrawnByUserId === content.authorUserId
      ? content.authorSummary?.name || content.authorSummary?.email || content.withdrawnByUserId
      : content.withdrawnByUserId;

  return (
    <VStack align="stretch" gap="10px" width="100%">
      <SectionTitle>Overview</SectionTitle>
      <VStack align="stretch" gap="8px">
        <InfoRow label="Type" value={getContentTypeLabel(content.type)} />
        <InfoRow label="Domains" value={getContentDomainsLabel(content.domains)} />
        <InfoRow
          label="Subdomains"
          value={content.subdomains?.map(formatSubdomainLabel).join(', ') || '/'}
        />
        <InfoRow label="Visibility" value={getContentVisibilityLabel(content.visibility)} />
        <InfoRow label="Platform content" value={content.isPlatformContent ? 'Yes (MyOmnia)' : 'No'} />
      </VStack>

      <Divider marginY="6px" />

      <SectionTitle>Author</SectionTitle>
      <VStack align="stretch" gap="8px">
        <InfoRow label="Name" value={content.authorSummary?.name} />
        <InfoRow label="Email" value={content.authorSummary?.email} />
        <InfoRow label="Roles" value={content.authorSummary?.roles?.join(', ')} />
        <InfoRow label="Organization" value={content.authorSummary?.organizationId} />
      </VStack>

      <Divider marginY="6px" />

      <SectionTitle>Timeline</SectionTitle>
      <VStack align="stretch" gap="8px">
        <InfoRow label="Created" value={formatContentDateTime(content.createdAt)} />
        <InfoRow label="Updated" value={formatContentDateTime(content.updatedAt)} />
        <InfoRow label="Published" value={formatContentDateTime(content.publishedAt)} />
      </VStack>

      {isRemoved && (
        <>
          <Divider marginY="6px" />
          <SectionTitle>Removal</SectionTitle>
          <VStack align="stretch" gap="8px">
            <InfoRow label="Removed at" value={formatContentDateTime(content.removedAt)} />
            <InfoRow label="Removed by" value={content.removedByUserId} />
            <InfoRow label="Reason" value={content.removalReason} />
          </VStack>
        </>
      )}

      {isWithdrawn && (
        <>
          <Divider marginY="6px" />
          <SectionTitle>Withdrawal</SectionTitle>
          <VStack align="stretch" gap="8px">
            <InfoRow label="Withdrawn at" value={formatContentDateTime(content.withdrawnAt)} />
            <InfoRow label="Withdrawn by" value={withdrawnBy} />
          </VStack>
          <Text fontSize="12px" color="text.mediumGray">
            The author took this down themselves. It is retained for review and cannot be restored.
          </Text>
        </>
      )}

      <Divider marginY="6px" />

      <SectionTitle>Audit log</SectionTitle>
      <AuditLogList entries={content.auditLog} />
    </VStack>
  );
};

export default ModerationInfoPanel;
